/**
 * Utility functions for exporting and importing rules as CSV
 */

/**
 * Escape a value for CSV (wrap in quotes if it contains commas, quotes, or newlines)
 */
const escapeCsvValue = (value) => {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

/**
 * Convert rules to CSV format.
 * Each condition becomes its own row. Condition groups share the same group number.
 * Columns: Rule Name, Category, Category ID, Priority, Group, Field, Operator, Value
 */
export const convertRulesToCSV = (rules, categories = []) => {
  const categoryMap = new Map();
  categories.forEach(cat => categoryMap.set(cat.id, cat.name));

  const headers = ['Rule ID', 'Rule Name', 'Category', 'Category ID', 'Priority', 'Group', 'Field', 'Operator', 'Value'];

  const rows = [];
  rules.forEach(rule => {
    const groups = rule.conditionGroups || (rule.conditions ? [{ conditions: rule.conditions }] : []);
    groups.forEach((group, groupIdx) => {
      (group.conditions || []).forEach(condition => {
        rows.push([
          escapeCsvValue(rule._id || rule.id || ''),
          escapeCsvValue(rule.name),
          escapeCsvValue(categoryMap.get(rule.categoryId) || 'Unknown'),
          escapeCsvValue(rule.categoryId),
          rule.priority,
          groupIdx + 1,
          escapeCsvValue(condition.field),
          escapeCsvValue(condition.operator),
          escapeCsvValue(condition.value)
        ].join(','));
      });
    });
  });

  return [headers.join(','), ...rows].join('\n');
};

/**
 * Download content as a file
 */
const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export all rules to CSV file
 */
export const exportRulesToCSV = (rules, categories) => {
  const csvContent = convertRulesToCSV(rules, categories);
  const filename = `rules_${new Date().toISOString().split('T')[0]}.csv`;
  downloadFile(csvContent, filename, 'text/csv');
};

/**
 * Parse a CSV string into rows of values
 */
const parseCSVLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
  }
  values.push(current.trim());
  return values;
};

/**
 * Parse imported rules from CSV content.
 * Groups rows back into rule objects with conditionGroups.
 */
export const parseImportedRulesCSV = (csvContent) => {
  const lines = csvContent.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV file must have a header row and at least one data row');
  }

  // Validate header — Rule Name and Rule ID are optional
  const header = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());
  const requiredHeaders = ['category', 'priority', 'group', 'field', 'operator', 'value'];
  const missingHeaders = requiredHeaders.filter(h => !header.includes(h));
  if (missingHeaders.length > 0) {
    throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
  }

  const colIdx = {
    id: header.indexOf('rule id'),
    name: header.indexOf('rule name'),
    category: header.indexOf('category'),
    categoryId: header.indexOf('category id'),
    priority: header.indexOf('priority'),
    group: header.indexOf('group'),
    field: header.indexOf('field'),
    operator: header.indexOf('operator'),
    value: header.indexOf('value'),
  };

  // Parse rows into a map keyed by rule ID/name + category ID + priority
  const rulesMap = new Map();

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    if (values.length < requiredHeaders.length) continue;

    const ruleId = colIdx.id >= 0 ? values[colIdx.id] : '';
    const name = colIdx.name >= 0 ? values[colIdx.name] : '';
    const categoryId = colIdx.categoryId >= 0 ? values[colIdx.categoryId] : '';
    const categoryName = colIdx.category >= 0 ? values[colIdx.category] : '';
    const priority = parseInt(values[colIdx.priority], 10);
    const groupNum = parseInt(values[colIdx.group], 10);
    const field = values[colIdx.field];
    const operator = values[colIdx.operator];
    const value = values[colIdx.value];

    if (!categoryName && !categoryId) throw new Error(`Row ${i + 1}: missing Category`);
    if (isNaN(priority) || priority < 1) throw new Error(`Row ${i + 1}: invalid Priority (must be >= 1)`);
    if (isNaN(groupNum) || groupNum < 1) throw new Error(`Row ${i + 1}: invalid Group (must be >= 1)`);
    if (!field) throw new Error(`Row ${i + 1}: missing Field`);
    if (!operator) throw new Error(`Row ${i + 1}: missing Operator`);

    const ruleKey = `${ruleId || name}||${categoryName || categoryId}||${priority}`;
    if (!rulesMap.has(ruleKey)) {
      rulesMap.set(ruleKey, {
        name,
        categoryId: categoryId || '',
        categoryName: categoryName || '',
        priority,
        conditionGroups: {}
      });
    }

    const rule = rulesMap.get(ruleKey);
    if (!rule.conditionGroups[groupNum]) {
      rule.conditionGroups[groupNum] = [];
    }
    rule.conditionGroups[groupNum].push({ field, operator, value });
  }

  // Convert conditionGroups from object to array
  const rules = Array.from(rulesMap.values()).map(rule => ({
    name: rule.name,
    categoryId: rule.categoryId,
    categoryName: rule.categoryName,
    priority: rule.priority,
    conditionGroups: Object.keys(rule.conditionGroups)
      .sort((a, b) => Number(a) - Number(b))
      .map(key => ({ conditions: rule.conditionGroups[key] }))
  }));

  if (rules.length === 0) {
    throw new Error('No valid rules found in CSV');
  }

  return rules;
};

/**
 * Resolve category IDs from category names and validate.
 * If a rule has a categoryId that matches, use it directly.
 * Otherwise, match by category name (case-insensitive).
 */
export const validateRuleCategories = (importedRules, categories) => {
  const categoryIds = new Set(categories.map(c => c.id));
  const categoryNameMap = new Map();
  categories.forEach(c => {
    categoryNameMap.set(c.name.toLowerCase().trim(), c.id);
  });
  const warnings = [];

  const validRules = importedRules
    .map(rule => {
      // If categoryId is provided and valid, use it
      if (rule.categoryId && categoryIds.has(rule.categoryId)) {
        return rule;
      }
      // Otherwise try to resolve by category name
      if (rule.categoryName) {
        const resolvedId = categoryNameMap.get(rule.categoryName.toLowerCase().trim());
        if (resolvedId) {
          return { ...rule, categoryId: resolvedId };
        }
      }
      warnings.push(`Rule "${rule.name || 'Unnamed'}": category "${rule.categoryName || rule.categoryId}" not found and will be skipped`);
      return null;
    })
    .filter(Boolean);

  return { validRules, warnings };
};

/**
 * Generate a CSV template string for users to see the expected format
 */
export const getCSVTemplate = () => {
  return `Rule Name,Category,Priority,Group,Field,Operator,Value
Grocery Shopping,Groceries,1,1,description,contains,Woolworths
Grocery Shopping,Groceries,1,1,amount,greater_than,20
Netflix Subscription,Subscriptions,2,1,description,contains,Netflix`;
};
